/**
 * Subscriber / CRM API
 *
 * Talks to the Express backend subscriber endpoints.
 * Uses REACT_APP_API_URL env var if set, otherwise empty (relative URL).
 * Relative URLs hit Vercel serverless functions in production or CRA proxy locally.
 * All public-facing calls (subscribe, trackRead) fail silently if server is down.
 */

const API = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const h = { 'Content-Type': 'application/json' };
  const stored = localStorage.getItem('fvl_auth');
  if (stored) {
    try {
      const u = JSON.parse(stored);
      if (u.credential) h['Authorization'] = `Bearer ${u.credential}`;
      else if (u.email) h['x-admin-email'] = u.email;
    } catch { /* ignore */ }
  }
  return h;
}

/** Public — subscribe a user (or update existing). Fails silently if server is unreachable. */
export async function subscribe({ email, name, picture, source, group, notes }) {
  try {
    const res = await fetch(`${API}/api/subscribers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, picture, source, group, notes }),
    });
    if (!res.ok) return { ok: false, error: `Server responded ${res.status}` };
    return res.json();
  } catch (err) {
    console.warn('CRM subscribe unavailable:', err.message);
    return { ok: false, error: 'Server unreachable' };
  }
}

/** Public — track article read. Fails silently if server is unreachable. */
export async function trackRead({ email, articleSlug, articleTitle }) {
  try {
    const res = await fetch(`${API}/api/subscribers/track-read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, articleSlug, articleTitle }),
    });
    if (!res.ok) return { ok: false };
    return res.json();
  } catch {
    return { ok: false };
  }
}

async function safeFetch(url, opts) {
  try {
    const res = await fetch(url, opts);
    if (!res.ok) {
      const text = await res.text();
      try { return JSON.parse(text); } catch { return { ok: false, error: `Server responded ${res.status}` }; }
    }
    return res.json();
  } catch (err) {
    console.warn('CRM API unreachable:', err.message);
    return { ok: false, error: 'Server unreachable', subscribers: [], groups: [], total: 0 };
  }
}

/** Admin — list subscribers */
export async function listSubscribers({ group, q } = {}) {
  const params = new URLSearchParams();
  if (group) params.set('group', group);
  if (q) params.set('q', q);
  return safeFetch(`${API}/api/subscribers?${params}`, { headers: authHeaders() });
}

/** Admin — get single subscriber */
export async function getSubscriber(id) {
  return safeFetch(`${API}/api/subscribers/${id}`, { headers: authHeaders() });
}

/** Admin — update subscriber */
export async function updateSubscriber(id, data) {
  return safeFetch(`${API}/api/subscribers/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
}

/** Admin — delete subscriber */
export async function deleteSubscriber(id) {
  return safeFetch(`${API}/api/subscribers/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

/** Admin — get group summary */
export async function getGroupSummary() {
  return safeFetch(`${API}/api/subscribers/groups/summary`, { headers: authHeaders() });
}

/** Admin — send email to group or specific recipients */
export async function sendEmail({ subject, htmlBody, group, recipientIds, mode }) {
  return safeFetch(`${API}/api/subscribers/send-email`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ subject, htmlBody, group, recipientIds, mode }),
  });
}

/** Admin — get email action log (paginated) */
export async function getEmailLog(page = 1, perPage = 20) {
  const params = new URLSearchParams({ page, perPage });
  return safeFetch(`${API}/api/subscribers/email-log?${params}`, { headers: authHeaders() });
}
